// src/AGB.js — AVS Cloud | Allgemeine Geschäftsbedingungen | Stand: Juni 2026
import React, { useEffect } from 'react';
import { setPageMeta } from './utils/seo';

const s = {
  wrap: { minHeight: '100vh', padding: '2rem clamp(1rem, 4vw, 2.5rem) 5rem', position: 'relative', zIndex: 1 },
  inner: { maxWidth: '760px', margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', textDecoration: 'none', marginBottom: '2.5rem', cursor: 'pointer' },
  title: { fontFamily: 'var(--font-head)', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 900, letterSpacing: '-0.04em', color: 'var(--white)', marginBottom: '0.4rem', lineHeight: 1.1 },
  subtitle: { fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '2.5rem', letterSpacing: '0.04em', paddingBottom: '2rem', borderBottom: '1px solid var(--border)' },
  block: { padding: '1.75rem 0', borderBottom: '1px solid var(--border)' },
  blockLast: { padding: '1.75rem 0' },
  heading: { fontSize: '0.95rem', fontWeight: 700, color: 'var(--cyan)', letterSpacing: '-0.01em', marginBottom: '0.85rem', fontFamily: 'var(--font-head)' },
  p: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, margin: 0 },
  pmt: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, marginTop: '0.75rem' },
  ul: { margin: '0.75rem 0 0 1.2rem', padding: 0 },
  li: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, paddingLeft: '0.3rem' },
  link: { color: 'var(--cyan)', textDecoration: 'underline', textUnderlineOffset: '3px' },
  strong: { color: 'var(--white)', fontWeight: 600 },
  widerrufBox: {
    background: 'rgba(0, 212, 255, 0.04)',
    border: '1px solid rgba(0, 212, 255, 0.2)',
    borderRadius: '8px',
    padding: '1.25rem 1.5rem',
    marginTop: '1rem',
  },
};

export default function AGB() {
  useEffect(() => {
    setPageMeta({
      title: 'AGB – Allgemeine Geschäftsbedingungen | AVS Cloud',
      description: 'Allgemeine Geschäftsbedingungen von AVS Cloud (AquaV Services, Mert Munz, Hamburg). Nutzungsbedingungen für den verschlüsselten Cloud-Speicherdienst.',
      path: '/agb',
      noindex: false,
    });
  }, []);
  return (
    <div style={s.wrap}>
      <div style={s.inner}>
        <a href="/" style={s.back}>← Zurück zur Cloud</a>
        <h1 style={s.title}>Allgemeine Geschäftsbedingungen</h1>
        <p style={s.subtitle}>AVS Cloud — Stand: Juni 2026</p>

        {/* §1 Geltungsbereich */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 1 — Geltungsbereich</h2>
          <p style={s.p}>
            Diese AGB gelten für alle Verträge zwischen{' '}
            <span style={s.strong}>Mert Munz</span> (Einzelunternehmen,
            c/o IP-Management #10553, Ludwig-Erhard-Str. 18, 20459 Hamburg, E-Mail:{' '}
            <a href="mailto:aquavservices@gmail.com" style={s.link}>
              aquavservices@gmail.com
            </a>
            ) und Verbrauchern (§ 13 BGB) über die Nutzung von AVS Cloud unter
            aquav-cloud.de. Für Unternehmer gelten abweichende Regelungen, sofern
            im Einzelfall vereinbart. Entgegenstehende AGB des Nutzers werden nicht
            anerkannt.
          </p>
        </div>

        {/* §2 Leistungsbeschreibung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 2 — Leistungsbeschreibung</h2>
          <p style={s.p}>
            AVS Cloud ist ein digitaler Cloud-Speicherdienst gemäß § 327 BGB
            (digitale Dienstleistung).
          </p>
          <ul style={s.ul}>
            <li style={s.li}>
              <span style={s.strong}>Gratis:</span> 5 GB nach Registrierung und
              Freischaltung durch den Betreiber.
            </li>
            <li style={s.li}>
              <span style={s.strong}>Kostenpflichtig:</span> Erweiterung in
              10-GB-Schritten bis max. 250 GB; 0,40 € pro 10 GB/Monat.
            </li>
          </ul>
          <p style={s.pmt}>
            Kein Anspruch auf ununterbrochene Verfügbarkeit. AVS Cloud ist{' '}
            <span style={s.strong}>kein Backup-Dienst</span> — Nutzer sind selbst
            für Sicherungskopien verantwortlich. Updates werden bereitgestellt,
            soweit technisch möglich und vernünftigerweise zu erwarten
            (§ 327f BGB).
          </p>
        </div>

        {/* §3 Vertragsschluss */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 3 — Vertragsschluss & Button-Lösung</h2>
          <p style={s.p}>
            Registrierung: Discord OAuth2 + Freigabe durch den Betreiber.
          </p>
          <p style={s.pmt}>
            Kostenpflichtige Erweiterung: Der Vertrag kommt durch Klick auf den
            Button{' '}
            <span style={s.strong}>"Zahlungspflichtig bestellen"</span> zustande
            (§ 312j BGB). Vor dem Button werden gemäß § 312j Abs. 2 BGB
            angezeigt: Leistung, Gesamtpreis, Laufzeit und Kündigungsbedingungen.
          </p>
          <p style={s.pmt}>Vertragssprache: Deutsch.</p>
        </div>

        {/* §4 Widerrufsrecht */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 4 — Widerrufsrecht & Widerrufsbutton</h2>
          <p style={s.p}>
            Als Verbraucher steht Ihnen bei online abgeschlossenen Verträgen über
            digitale Dienstleistungen ein 14-tägiges Widerrufsrecht zu (§ 355 BGB).
          </p>

          <div style={s.widerrufBox}>
            <p style={{ ...s.p, color: 'var(--white)', fontWeight: 600, marginBottom: '0.75rem' }}>
              WIDERRUFSBELEHRUNG
            </p>
            <p style={s.p}>
              Sie haben das Recht, diesen Vertrag binnen 14 Tagen ohne Angabe von
              Gründen zu widerrufen. Die Widerrufsfrist beträgt 14 Tage ab dem Tag
              des Vertragsschlusses.
            </p>
            <p style={s.pmt}>
              Um Ihr Widerrufsrecht auszuüben, müssen Sie uns (Mert Munz,
              c/o IP-Management #10553, Ludwig-Erhard-Str. 18, 20459 Hamburg, E-Mail:{' '}
              <a href="mailto:aquavservices@gmail.com" style={s.link}>
                aquavservices@gmail.com
              </a>
              ) mittels einer eindeutigen Erklärung (z.B. über den Widerrufsbutton
              auf unserer Website, per E-Mail oder per Brief) über Ihren Entschluss,
              diesen Vertrag zu widerrufen, informieren. Sie können dafür den
              Widerrufsbutton unter{' '}
              <a href="/widerruf" style={s.link}>
                aquav-cloud.de/widerruf
              </a>{' '}
              nutzen.
            </p>
            <p style={s.pmt}>
              Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die Mitteilung
              über die Ausübung des Widerrufsrechts vor Ablauf der Widerrufsfrist
              absenden.
            </p>
          </div>

          <p style={{ ...s.p, fontWeight: 600, color: 'var(--white)', marginTop: '1rem' }}>
            WIDERRUFSBUTTON (§ 356a BGB — ab 19.06.2026):
          </p>
          <p style={s.p}>
            Auf{' '}
            <a href="/widerruf" style={s.link}>
              aquav-cloud.de/widerruf
            </a>{' '}
            steht Ihnen jederzeit (auch ohne Login) ein elektronischer
            Widerrufsbutton zur Verfügung. Der Widerruf erfolgt zweistufig
            ("Vertrag widerrufen" → "Widerruf bestätigen"). Sie erhalten sofort
            eine Eingangsbestätigung per E-Mail (Bestätigung des Eingangs, nicht
            der Wirksamkeit).
          </p>

          <p style={{ ...s.p, fontWeight: 600, color: 'var(--white)', marginTop: '1rem' }}>
            ERLÖSCHEN DES WIDERRUFSRECHTS (§ 356 Abs. 5 BGB):
          </p>
          <p style={s.p}>
            Das Widerrufsrecht erlischt vorzeitig, wenn Sie ausdrücklich zugestimmt
            haben, dass wir mit der Ausführung des Vertrags vor Ablauf der
            Widerrufsfrist beginnen, UND Sie bestätigt haben, dass Sie zur Kenntnis
            genommen haben, dass Sie Ihr Widerrufsrecht durch Ihre Zustimmung
            verlieren. Diese Zustimmung erfolgt durch eine separate, nicht
            vorausgefüllte Checkbox beim Checkout.
          </p>

          <p style={{ ...s.p, fontWeight: 600, color: 'var(--white)', marginTop: '1rem' }}>
            FOLGEN DES WIDERRUFS:
          </p>
          <p style={s.p}>
            Im Falle eines wirksamen Widerrufs erhalten Sie bereits geleistete
            Zahlungen anteilig für nicht erbrachte Tage zurück. Erstattungen
            erfolgen über das ursprüngliche Zahlungsmittel.
          </p>
        </div>

        {/* §5 Preise & Zahlung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 5 — Preise & Zahlung</h2>
          <p style={s.p}>
            Alle Preise in Euro. Gemäß § 19 Abs. 1 UStG (Kleinunternehmerregelung)
            wird keine Umsatzsteuer auf Rechnungen ausgewiesen.
          </p>
          <p style={s.pmt}>
            Zahlung per Stripe (Kreditkarte) oder PayPal, monatlich im Voraus.
            Buchungen im laufenden Monat werden anteilig berechnet (Prorated).
            Rechnungen nach § 14 UStG werden per E-Mail und im Dashboard
            bereitgestellt.
          </p>
          <p style={s.pmt}>
            Vor der ersten Buchung ist die Angabe einer Rechnungsadresse
            erforderlich (§ 14 UStG).
          </p>
        </div>

        {/* §6 Kündigung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 6 — Kündigung (§ 312k BGB — Kündigungsbutton)</h2>
          <p style={s.p}>
            <span style={s.strong}>Ordentliche Kündigung:</span> Jederzeit zum Ende
            des laufenden Monats über den Kündigungsbutton in den Einstellungen
            (zweistufig: "Abo kündigen" → "Kündigung bestätigen"). Nach Kündigung
            läuft der Speicher bis zum Monatsende weiter.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Außerordentliche Kündigung:</span> Bei
            wesentlichen AGB-Änderungen mit 30 Tagen Frist.
          </p>
          <p style={s.pmt}>
            Bei Kontolöschung werden alle Abonnements sofort gekündigt. Keine
            weiteren Abbuchungen nach Kündigung.
          </p>
        </div>

        {/* §7 Nutzerpflichten */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 7 — Nutzerpflichten & verbotene Inhalte</h2>
          <p style={s.p}>Verboten sind Inhalte, die:</p>
          <ul style={s.ul}>
            {[
              'gegen geltendes Recht verstoßen (StGB, UrhG, JMStV etc.)',
              'kinderpornografisches oder jugendgefährdendes Material darstellen',
              'Terrorismus, Volksverhetzung oder Hasskriminalität fördern',
              'Urheberrechte Dritter verletzen',
              'Malware, Viren oder schädlichen Code enthalten',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Der Nutzer ist allein verantwortlich für hochgeladene Inhalte. Bei
            Verstößen: sofortige Sperrung oder Löschung des Accounts ohne
            Erstattung laufender Gebühren. Bei Straftaten werden Behörden
            informiert.
          </p>
          <p style={s.pmt}>
            Alle Uploads werden automatisch auf Schadsoftware geprüft.
            Dateien, die als schädlich erkannt werden, werden blockiert und
            nicht gespeichert. Details zur Verarbeitung in der Datenschutzerklärung (§ 6).
          </p>
          <p style={{ ...s.p, fontWeight: 600, color: 'var(--white)', marginTop: '1rem' }}>
            MELDUNG ILLEGALER INHALTE:
          </p>
          <p style={s.p}>
            Illegale Inhalte können über den Melde-Button auf Share-Seiten oder
            per E-Mail an{' '}
            <a href="mailto:aquavservices@gmail.com?subject=Abuse" style={s.link}>
              aquavservices@gmail.com
            </a>{' '}
            (Betreff: Abuse) gemeldet werden. Meldungen werden unverzüglich
            geprüft (klare rechtswidrige Inhalte: sofort; komplexe Fälle: max.
            7 Tage).
          </p>
        </div>

        {/* §7a VPN/Proxy */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 7a — VPN- und Proxy-Nutzung</h2>
          <p style={s.p}>
            Der Zugang zu AVS Cloud über VPN-Dienste, Proxy-Server, Tor-Netzwerke,
            Anonymisierungsdienste oder Rechenzentrumsadressen ist{' '}
            <span style={s.strong}>nicht gestattet</span>. Verbindungsversuche über
            solche Dienste werden automatisch erkannt und abgewiesen.
          </p>
          <p style={s.pmt}>
            Grund: Schutz vor Missbrauch, Spam und Umgehung von Sicherheitsmaßnahmen.
            Rechtsgrundlage für die IP-Prüfung: Art. 6 Abs. 1 lit. f DSGVO
            (berechtigtes Interesse an der Sicherheit des Dienstes).
          </p>
          <p style={s.pmt}>
            Sollte Ihre IP-Adresse fälschlicherweise als VPN/Proxy eingestuft worden
            sein, wenden Sie sich bitte an{' '}
            <a href="mailto:aquavservices@gmail.com" style={s.link}>
              aquavservices@gmail.com
            </a>
            .
          </p>
        </div>

        {/* §7b IP-Logging */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 7b — IP-Adressen-Protokollierung bei Login und Logout</h2>
          <p style={s.p}>
            Beim An- und Abmelden wird die IP-Adresse des Nutzers protokolliert.
            Diese Daten werden verwendet, um unberechtigte Zugriffe zu erkennen und
            zu verhindern sowie für die Nachvollziehbarkeit von Sicherheitsereignissen.
          </p>
          <p style={s.pmt}>
            Die Protokolldaten werden für maximal{' '}
            <span style={s.strong}>90 Tage</span> gespeichert und anschließend
            automatisch gelöscht. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO.
            Weitere Details in der{' '}
            <a href="/datenschutz" style={s.link}>
              Datenschutzerklärung
            </a>
            .
          </p>
        </div>

        {/* §8 Haftung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 8 — Haftung (§ 307 BGB konform — differenziert)</h2>
          <p style={s.p}>
            Der Betreiber haftet{' '}
            <span style={s.strong}>uneingeschränkt</span> für:
          </p>
          <ul style={s.ul}>
            {[
              'Vorsatz und grobe Fahrlässigkeit',
              'Schäden aus der Verletzung von Leben, Körper oder Gesundheit',
              'Ansprüche nach dem Produkthaftungsgesetz',
              'Übernahme einer Garantie',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Bei{' '}
            <span style={s.strong}>einfacher (leichter) Fahrlässigkeit</span>{' '}
            haftet der Betreiber nur, wenn eine Kardinalpflicht (wesentliche
            Vertragspflicht, deren Erfüllung die ordnungsgemäße Durchführung des
            Vertrags erst ermöglicht und auf deren Einhaltung der Nutzer
            regelmäßig vertraut) verletzt wird — und dann nur für den bei
            Vertragsschluss vorhersehbaren, vertragstypischen Schaden.
          </p>
          <p style={s.pmt}>
            Bei einfacher Fahrlässigkeit ohne Verletzung einer Kardinalpflicht ist
            die Haftung ausgeschlossen.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>DATENVERLUST:</span> Bei Datenverlust haftet
            der Betreiber nur bei Vorsatz oder grober Fahrlässigkeit. AVS Cloud
            ist kein Backup-Dienst.
          </p>
        </div>

        {/* §9 Datenschutz */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 9 — Datenschutz</h2>
          <p style={s.p}>
            Es gilt die{' '}
            <a href="/datenschutz" style={s.link}>
              Datenschutzerklärung
            </a>{' '}
            unter aquav-cloud.de/datenschutz. Zahlungsdaten werden ausschließlich
            durch Stripe bzw. PayPal verarbeitet.
          </p>
        </div>

        {/* §10 Kleinunternehmerregelung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 10 — Kleinunternehmerregelung (§ 19 UStG)</h2>
          <p style={s.p}>
            Mert Munz ist Kleinunternehmer gemäß § 19 Abs. 1 UStG. Auf Rechnungen
            wird keine Umsatzsteuer ausgewiesen.
          </p>
        </div>

        {/* §11 OSS */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 11 — OSS / Steuer EU-Ausland</h2>
          <p style={s.p}>
            Für Kunden in anderen EU-Mitgliedstaaten gelten möglicherweise
            abweichende umsatzsteuerliche Regelungen ab einem EU-Jahresumsatz von
            10.000 €. Aktuell liegt der EU-Auslandsumsatz unter dieser Grenze
            (Heimatlandprinzip).
          </p>
        </div>

        {/* §12 AGB-Änderungen */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12 — AGB-Änderungen</h2>
          <p style={s.p}>
            Änderungen werden mit 30 Tagen Vorlauf per E-Mail angekündigt.
            Widerspruchsrecht: außerordentliche Kündigung zum Änderungszeitpunkt.
            Keine Reaktion innerhalb der Frist gilt als Zustimmung — auf diesen
            Mechanismus wird bei der Ankündigung ausdrücklich hingewiesen.
          </p>
        </div>

        {/* §13 Schlussbestimmungen */}
        <div style={s.blockLast}>
          <h2 style={s.heading}>§ 13 — Schlussbestimmungen</h2>
          <p style={s.p}>
            Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts (CISG).
            Gerichtsstand für Kaufleute: Rastatt. Für Verbraucher gilt der
            gesetzliche Gerichtsstand (§ 29c ZPO). Sollten einzelne Bestimmungen
            dieser AGB unwirksam oder undurchführbar sein oder werden, bleibt die
            Wirksamkeit der übrigen Bestimmungen davon unberührt (salvatorische
            Klausel).
          </p>
        </div>
      </div>
    </div>
  );
}
